import type { Seat } from '@campfire/engine';
import { HOME, NEST, RING_END, ringSquare, type ParcheesiEvent, type ParcheesiMove, type ParcheesiView, type Pip } from '@campfire/game-parcheesi';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { die, randomFace, tumbling } from './dice.ts';
import { pawnShape, seatColor, svg } from './track-common.ts';

const U = 20;
const SIZE = 19 * U;
const NEUTRAL = '#9a9a9a';

/** Rotate a point (y, x in cell units) a quarter turn counterclockwise about the centre, a times. */
function rot(a: number, y: number, x: number): [number, number] {
  let p: [number, number] = [y, x];
  for (let i = 0; i < a; i++) p = [19 - p[1], p[0]];
  return p;
}
/** Ring square of the base arm (bottom): the visual index i = 0..16, counterclockwise. */
function armCell(i: number): [number, number] {
  if (i <= 7) return [11 + i, 8];
  if (i === 8) return [18, 9];
  return [18 - (i - 9), 10];
}
const centreOf = ([r, c]: [number, number]): [number, number] => [r + 0.5, c + 0.5];

/** Cell-unit centre of a pawn at `progress` for a seat whose arm is `arm`. `slot` spreads pawns on one square. */
export function spot(arm: number, progress: number, index: number): [number, number] {
  if (progress === NEST) {
    const off: [number, number][] = [[13.8, 13.8], [13.8, 16.2], [16.2, 13.8], [16.2, 16.2]];
    const [y, x] = off[index % 4] as [number, number];
    return rot(arm, y, x);
  }
  if (progress === HOME) return rot(arm, 10.5, 9.5);
  if (progress > RING_END) return rot(arm, 17.5 - (progress - RING_END - 1), 9.5);
  const v = (ringSquare(arm, progress) + 9) % 68;
  return rot(Math.floor(v / 17), ...centreOf(armCell(v % 17)));
}
const px = (p: [number, number]) => ({ x: p[1] * U, y: p[0] * U });

const STAR = (cx: number, cy: number, r: number): string =>
  Array.from({ length: 10 }, (_, k) => {
    const a = (Math.PI / 5) * k - Math.PI / 2;
    const rr = k % 2 === 0 ? r : r * 0.45;
    return `${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a)).toFixed(1)}`;
  }).join(' ');

function boardStatic(v: ParcheesiView): SVGElement[] {
  const parts: SVGElement[] = [svg('rect', { x: 0, y: 0, width: SIZE, height: SIZE, rx: 10, class: 'pc-bg' })];
  for (let a = 0; a < 4; a++) {
    const seat = v.arms.indexOf(a);
    const col = seat >= 0 ? seatColor(seat) : NEUTRAL;
    const g: SVGElement[] = [];
    for (let i = 0; i < 17; i++) {
      const [r, c] = armCell(i);
      const entry = i === 13;
      g.push(svg('rect', { x: c * U, y: r * U, width: U, height: U, class: 'pc-sq', ...(entry ? { style: `fill:${col};fill-opacity:.55` } : {}) }));
      if (i === 3 || i === 8 || i === 13) {
        const [cy, cx] = centreOf([r, c]);
        g.push(svg('polygon', { points: STAR(cx * U, cy * U, 7), class: 'pc-star', 'data-safe': `${a}-${i}` }));
      }
    }
    for (let k = 0; k < 7; k++) g.push(svg('rect', { x: 9 * U, y: (11 + k) * U, width: U, height: U, style: `fill:${col};fill-opacity:.4`, class: 'pc-sq' }));
    g.push(
      svg('rect', { x: 9 * U, y: 10 * U, width: U, height: U, fill: col, class: 'pc-pad' }),
      svg('rect', { x: 11.2 * U, y: 11.2 * U, width: 7.2 * U, height: 7.2 * U, rx: 14, fill: col, 'fill-opacity': 0.3, class: 'pc-nest' }),
    );
    parts.push(svg('g', { transform: `rotate(${-90 * a} ${SIZE / 2} ${SIZE / 2})`, 'data-arm': a }, ...g));
  }
  return parts;
}

const pipText = (p: Pip) => (p.kind === 'bonus' ? `+${p.value}` : String(p.value));

export function moveText(ctx: TableCtx, v: ParcheesiView, m: ParcheesiMove): string {
  const a = m.action;
  if (a.type === 'enter') return `Bring a pawn out (${a.use.map((i) => v.remaining[i]?.value ?? '?').join(' + ')})${m.captures ? ' · captures!' : ''}`;
  const pip = a.type === 'move' ? v.remaining[a.use] : undefined;
  return `Pawn ${m.pawn + 1} forward ${m.to - m.from}${pip?.kind === 'bonus' ? ' (bonus)' : ''}${m.captures ? ' · captures!' : ''}${m.to === HOME ? ' · Home!' : m.to > RING_END && m.from <= RING_END ? ' · home column' : ''}`;
}

export function parcheesiEvent(ctx: TableCtx, e: ParcheesiEvent | null, turn: Seat): string {
  if (!e) return `${name(ctx, turn)} ${turn === ctx.you ? 'go' : 'goes'} first. Roll the dice!`;
  const who = name(ctx, e.seat);
  const dice = e.dice ? `${e.dice[0]} and ${e.dice[1]}${e.doubles ? ' (doubles)' : ''}` : '';
  const cap = e.captured ? ` and captured ${name(ctx, e.captured.seat)} pawn ${e.captured.pawn + 1} (+20)` : '';
  switch (e.kind) {
    case 'rolled': return `${who} rolled ${dice}.`;
    case 'forfeit': return `${who} rolled ${dice} but has no move.${e.doubles ? ' Doubles: roll again.' : ''}`;
    case 'penalty': return `${who} rolled doubles a third time! ${e.sentBack !== null ? `Pawn ${e.sentBack + 1} goes back to the nest.` : 'Nothing to send back.'}`;
    case 'entered': return `${who} brought pawn ${(e.pawn ?? 0) + 1} out${cap}.`;
    default: return `${who} moved pawn ${(e.pawn ?? 0) + 1} ${e.to - e.from}${cap}${e.to === HOME ? ' and reached home (+10)' : ''}.`;
  }
}

export const parcheesiUi: GameUi = {
  id: 'parcheesi',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const row = (ctx.msg.view as ParcheesiView).pawns[s] ?? [];
    return `${row.filter((p) => p === HOME).length}/${row.length} home`;
  },
  render(ctx) {
    const v = ctx.msg.view as ParcheesiView;
    const over = v.result !== null;
    // A fresh roll tumbles for everyone before the moves appear (FR-DICE-01).
    const rolling = tumbling(ctx, 'pc-roll', `${ctx.msg.seq}`, v.last?.kind === 'rolled' || v.last?.kind === 'forfeit' || v.last?.kind === 'penalty');
    const mine = ctx.myTurn && !over && !rolling;
    const key = JSON.stringify([v.pawns, v.remaining, v.turn]);
    const movable = [...new Set(v.legal.map((m) => m.pawn))].sort();
    const stored = ctx.local.get<{ key: string; pawn: number }>('sel');
    const sel = stored && stored.key === key && movable.includes(stored.pawn) ? stored.pawn : movable.length === 1 ? (movable[0] as number) : null;
    const select = (pawn: number) => ctx.local.set('sel', { key, pawn });
    const shown = sel === null ? [] : v.legal.filter((m) => m.pawn === sel);

    const arm = v.arms[v.turn] as number;
    const pawns: SVGElement[] = [];
    v.pawns.forEach((row, s) => {
      const a = v.arms[s] as number;
      const seen = new Map<number, number>();
      row.forEach((pos, i) => {
        if (pos === HOME) return;
        const n = seen.get(pos) ?? 0;
        seen.set(pos, n + 1);
        const c = px(spot(a, pos, pos === NEST ? i : 0));
        const x = c.x + (pos === NEST ? 0 : n * 10 - (row.filter((p) => p === pos).length - 1) * 5);
        const can = mine && s === v.turn && movable.includes(i);
        const g = svg('g', {
          class: `pc-pawn${can ? ' can' : ''}${can && sel === i ? ' sel' : ''}`,
          'data-testid': `parcheesi-pawn-${s}-${i}`, 'data-progress': pos, role: can ? 'button' : undefined, 'aria-label': `${name(ctx, s)} pawn ${i + 1}`,
        }, pawnShape(s, x, c.y, 7.5, { class: 'pc-body' }), svg('text', { x, y: c.y + 3.5, class: 'pc-num' }, String(i + 1)));
        if (can) g.addEventListener('click', () => select(i));
        pawns.push(g);
      });
    });
    const homes: SVGElement[] = v.pawns.flatMap((row, s) => {
      const n = row.filter((p) => p === HOME).length;
      if (n === 0) return [];
      const c = px(spot(v.arms[s] as number, HOME, 0));
      return [svg('g', { 'data-testid': `parcheesi-home-${s}`, 'data-count': n }, svg('text', { x: c.x, y: c.y + 4, class: 'pc-home-n' }, String(n)))];
    });
    const dests: SVGElement[] = [];
    if (mine && sel !== null) {
      const used = new Set<number>();
      shown.forEach((m, k) => {
        if (used.has(m.to)) return;
        used.add(m.to);
        const c = px(spot(arm, m.to, 0));
        const t = svg('g', { class: 'pc-dest', 'data-testid': `parcheesi-dest-${k}`, role: 'button', 'aria-label': moveText(ctx, v, m) }, svg('circle', { cx: c.x, cy: c.y, r: 14 }),
          // Labelled like Sorry!'s targets: how far the pawn goes, or In for entering (FR-PARCHEESI-17).
          svg('text', { x: c.x, y: c.y + 4, class: 'pc-dest-txt' }, m.action.type === 'enter' ? 'In' : `+${m.to - m.from}${m.captures ? '!' : ''}`));
        t.addEventListener('click', () => ctx.act(m.action));
        dests.push(t);
      });
    }

    const canRoll = mine && v.phase === 'roll';
    return h(
      'div.parcheesi-table',
      {},
      h('div.pc-panel', {},
        h('div.event', { 'data-testid': 'event' }, rolling ? `${name(ctx, v.last?.seat ?? v.turn)} is rolling…` : parcheesiEvent(ctx, v.last, v.turn)),
        h(`div.pc-dice${rolling ? '.rolling' : ''}`, { 'data-testid': 'parcheesi-dice', 'data-rolling': rolling ? 'true' : undefined },
          (v.dice ?? [0, 0]).map((d, i) => {
            const el = die(rolling ? randomFace() : d, { testid: `parcheesi-die-${i}` });
            el.dataset['value'] = String(d);
            return el;
          }),
          h('div.pc-left', { 'data-testid': 'parcheesi-left' }, v.remaining.length ? ['To play: ', ...v.remaining.map((p) => h(`span.pc-pip${p.kind === 'bonus' ? '.bonus' : ''}`, {}, pipText(p)))] : null)),
        h('button.primary.pc-roll', { 'data-testid': 'parcheesi-roll', disabled: !canRoll, onclick: () => ctx.act({ type: 'roll' }) },
          over ? 'Game over' : canRoll ? 'Roll the dice' : mine ? 'Choose a move' : `${name(ctx, v.turn)} plays…`),
        mine && v.phase === 'move' && movable.length > 1
          ? h('div.pc-chips', {}, movable.map((i) => h(`button.pc-chip${sel === i ? '.on' : ''}`, { 'data-testid': `parcheesi-chip-${i}`, onclick: () => select(i) }, `Pawn ${i + 1}`)))
          : null,
        mine && v.phase === 'move'
          ? h('div.pc-moves', { 'data-testid': 'parcheesi-moves' }, [
              ...shown.map((m, k) => h('button.pc-move', { 'data-testid': `parcheesi-move-${k}`, onclick: () => ctx.act(m.action) }, moveText(ctx, v, m))),
              sel === null && movable.length > 1 ? h('div.muted', {}, 'Pick a pawn (tap it or a button) to see its moves.') : null,
            ])
          : null),
      h('div.pc-board-wrap', {}, svg('svg', { viewBox: `0 0 ${SIZE} ${SIZE}`, class: 'pc-board', 'data-testid': 'parcheesi-board', role: 'img', 'aria-label': 'Parcheesi board' },
        ...boardStatic(v), ...homes, ...pawns, ...dests)),
      h('div.pc-legend', {}, v.pawns.map((row, s) =>
        h(`div.pc-key${s === v.turn && !over ? '.active' : ''}`, { 'data-testid': `key-${s}` }, h('svg.pc-swatch', { viewBox: '-12 -12 24 24' }, pawnShape(s, 0, 0, 8)), `${name(ctx, s)} · ${row.filter((p) => p === HOME).length}/${row.length} home`))),
      h('div.muted', {}, 'Stars are safe squares. Two of your pawns on a square block everyone.'),
    );
  },
};
